import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  MonitorSmartphone, CheckCircle2, ArrowRight, ChevronDown, Sparkles,
} from 'lucide-react';
import PageGlow from '@/components/PageGlow';
import HeroWave from '@/components/HeroWave';
import LazyDemoSection from '@/components/portal/LazyDemoSection';
import ServiceFinalBanner from '@/components/ServiceFinalBanner';

const GRAD    = '#5048E5';
const fg      = 'hsl(var(--foreground))';
const fgMuted = 'hsl(var(--muted-foreground))';
const surface = 'hsl(var(--card))';
const border  = 'hsl(var(--border))';
const color   = 'hsl(243 75% 59%)';
const FONT    = 'Plus Jakarta Sans, sans-serif';
const HEAD    = 'Outfit, sans-serif';

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
};
const stagger = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.09 } },
};

export const seo = {
  title: 'Branded Client Portal & App',
  tagline: 'Your own portal, your branding',
  metaDesc:
    'Your own branded client portal and mobile app — powered by the same system that runs Digitally Baffled. Clients log in to track progress, sign agreements, pay invoices, message you and book calls. Setup from £995, £75/month care plan.',
  slug: 'client-portal',
  whatWeDoItems: [
    { heading: 'Live project progress', body: 'Your clients log in and see exactly where their project is up to — every milestone, in real time. No more "any update?" emails.' },
    { heading: 'Sign agreements online', body: 'Send service agreements straight to the portal for a legally-binding e-signature. No more chasing signatures by email.' },
    { heading: 'Pay invoices by card', body: 'Clients pay invoices in a couple of taps, by card, straight to your account. No more "did you get my invoice?".' },
    { heading: 'Direct messaging & calls', body: 'Message clients directly, share files and voice notes, and even take calls — all in one place instead of scattered across texts and inboxes.' },
    { heading: 'Booking & documents', body: 'Clients book calls into your calendar and access all their documents whenever they need them — everything in one tidy place.' },
    { heading: 'Fully under your brand', body: 'Your logo, your colours, your domain. It looks and feels like your own software, because it is — clients never see ours.' },
  ],
};

/** Baseline features — everything every tier gets. Tier-specific extras
    (booking, e-sign, AI, calls, app store) live in the pricing tiers below. */
const whatYouGet = [
  { heading: 'Live project progress', body: 'Your clients log in and see exactly where their project is up to — every milestone, in real time. No more "any update?" emails.' },
  { heading: 'Pay invoices by card', body: 'Clients pay invoices in a couple of taps, by card, straight to your account. No more "did you get my invoice?".' },
  { heading: 'Messaging & documents', body: 'Message clients directly, share files and voice notes, and give them one tidy place to find every document you\'ve sent.' },
  { heading: 'Fully under your brand', body: 'Your logo, your colours, your domain. It looks and feels like your own software, because it is — clients never see ours.' },
];

const howItWorks = [
  { title: 'Discovery call', body: 'We show you the portal live, learn how you work with clients, and agree exactly what you need.' },
  { title: 'We brand it as yours', body: 'Your logo, your colours, your domain. We rebrand the whole thing so it looks and feels like your own software.' },
  { title: 'We set up your clients', body: 'We load in your services, your pricing, and help you invite your existing clients across so nothing gets lost.' },
  { title: 'You launch', body: 'You go live under your brand. On the Branded App tier we handle the App Store and Google Play submissions too.' },
];

const tiers = [
  {
    name: 'Portal',
    monthly: '£75',
    setup: '£995 setup',
    founding: true,
    popular: false,
    intro: null as string | null,
    features: [
      'Branded client portal (your logo and colours)',
      'Client messaging and document sharing',
      'Project progress tracking',
      'Invoicing with online card payment',
      'Email support',
    ],
  },
  {
    name: 'Portal Plus',
    monthly: '£150',
    setup: '£1,495 setup',
    founding: true,
    popular: false,
    intro: 'Everything in Portal, plus:' as string | null,
    features: [
      'Online booking with calendar sync',
      'E-signable service agreements',
      'AI assistant for you and your clients (fair-use limits apply)',
      'Priority support',
    ],
  },
  {
    name: 'Branded App',
    monthly: '£250',
    setup: '£2,995 setup',
    founding: false,
    popular: true,
    intro: 'Everything in Portal Plus, plus:' as string | null,
    features: [
      'Your own branded app in the App Store and Google Play',
      'In-app video and voice calls',
      'AI meeting summaries and call transcription',
      "Push notifications to your clients' phones",
      'We handle app store submissions and updates',
    ],
  },
];

const addOns = [
  { title: 'Custom & bespoke features', price: 'priced on scope', body: 'Need something extra built into your portal — integrations, custom workflows, reporting, or functionality unique to your business? We\u2019ll scope it and quote a one-off build cost.' },
];

const faqs = [
  { q: 'Who owns it?', a: 'You license the software, but your branding, your domain, your client data and your payment account are all yours. Your clients and their information belong to you, always.' },
  { q: 'How long does setup take?', a: 'Typically 1–2 weeks from the moment we start — we rebrand everything, load in your services and pricing, connect your domain and payments, and hand it over ready to use. The Branded App plan adds a little longer for app store review and submission.' },
  { q: 'What are the AI fair-use limits?', a: 'The AI assistant (on Portal Plus and Branded App) is covered by a generous monthly fair-use allowance that comfortably suits normal day-to-day use for you and your clients. If your usage is unusually heavy we\u2019ll simply have a chat about the best option — you\u2019ll never get a surprise bill.' },
  { q: 'Can I upgrade tiers later?', a: 'Yes — you can move up a tier whenever you\u2019re ready. We switch on the extra features and only charge the difference; there\u2019s no need to start over, and nothing you\u2019ve set up is lost.' },
  { q: 'What kind of businesses is it for?', a: 'Trades, consultants, accountants, agencies — anyone who invoices clients and manages projects. If you deal with clients and paperwork, it will save you time.' },
  { q: 'Can clients pay invoices through it?', a: 'Yes. Clients pay invoices by card directly through the portal, and the money goes straight to your account.' },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl transition-all" style={{ background: surface, border: `1px solid ${border}` }}>
      <button onClick={() => setOpen(v => !v)} className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left" aria-expanded={open}>
        <span className="text-sm font-semibold leading-snug" style={{ fontFamily: FONT, color: fg }}>{q}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0">
          <ChevronDown size={16} style={{ color: fgMuted }} />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: 'easeInOut' }} style={{ overflow: 'hidden' }}>
            <p className="px-6 pb-5 text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ClientPortalPage() {
  return (
    <>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-6 md:py-10" style={{ background: 'hsl(var(--background))' }}>
        <HeroWave />
        <PageGlow color={color} tall />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp}>
              <span className="mb-3 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 text-xs font-semibold"
                style={{ background: `${color}15`, border: `1px solid ${color}30`, color, fontFamily: FONT }}>
                <MonitorSmartphone size={13} strokeWidth={2.25} />
                {seo.tagline}
              </span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="mb-4 text-4xl font-bold md:text-5xl"
              style={{ fontFamily: HEAD, color: fg, lineHeight: 1.06 }}>
              {seo.title}
            </motion.h1>
            <motion.p variants={fadeUp} className="mb-6 text-lg leading-relaxed"
              style={{ fontFamily: FONT, color: fgMuted }}>
              This is your own branded client portal and mobile app — powered by the exact system that runs Digitally Baffled. Your clients log in to see project progress, sign agreements, pay invoices, message you directly, book calls and access their documents — all under your branding, your domain, your colours.
            </motion.p>
            <motion.div variants={fadeUp} className="flex items-center justify-center">
              <Link to="/get-started"
                className="btn-glow inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-all"
                style={{ background: GRAD, color: '#ffffff', fontFamily: FONT }}>
                Book a free demo <ArrowRight size={15} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── The problem / who it's for — merged, no blue slab ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.35 }}
          className="mx-auto max-w-3xl px-6 text-center">
          <span className="mb-3 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 text-xs font-semibold"
            style={{ background: `${color}15`, border: `1px solid ${color}30`, color, fontFamily: FONT }}>
            The problem
          </span>
          <h2 className="mb-6 text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg, lineHeight: 1.12 }}>
            Client work scattered across texts, email and paper
          </h2>
          <p className="mx-auto mb-4 max-w-2xl text-base leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
            Chasing signatures by email. &ldquo;Did you get my invoice?&rdquo; texts. Documents buried in inboxes. Progress updates repeated three times over. It eats hours you don&apos;t have — and it makes even a good business look scrappy to clients.
          </p>
          <p className="mx-auto mb-4 max-w-2xl text-base leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
            This is for trades, consultants, accountants and agencies — anyone who invoices clients and manages projects. It brings everything under one roof, under your brand, so clients get a slick experience and you stop losing evenings to admin. It&apos;s the exact portal we use with our own clients, so you can try the live demo below before you decide.
          </p>
        </motion.div>
      </section>

      {/* ── Try it yourself — embedded demo ── */}
      <section className="pb-4" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto flex max-w-5xl flex-col items-center px-6">
          <div className="mb-6 text-center">
            <h2 className="mb-2 text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg }}>
              Try it yourself
            </h2>
            <p className="mx-auto max-w-2xl text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
              This is the exact portal we use with our own clients. Switch between the <strong>admin view</strong> (your side) and the <strong>client view</strong> (theirs) — changes you make on one side show up on the other. It&apos;s a demo, so nothing is saved.
            </p>
          </div>
          <LazyDemoSection
            load={() => import('../../components/portal/PortalDemo')}
            className="w-full max-w-5xl"
            minHeight="min(760px, 82vh)"
          />
        </div>
      </section>

      {/* ── Bridge link after demo ── */}
      <section className="pb-8 pt-4" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
            Like what you see?{' '}
            <Link
              to="/get-started"
              className="font-semibold underline underline-offset-4 transition-colors hover:opacity-80"
              style={{ color: GRAD }}
            >
              Book a demo and we&apos;ll show you it under your brand →
            </Link>
          </p>
        </div>
      </section>

      {/* ── What you get — universal baseline only ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl px-6">
          <div className="mb-10 text-center">
            <h2 className="mb-3 text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg }}>
              What you get
            </h2>
            <p className="mx-auto max-w-2xl text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
              Every plan includes the essentials below. Tier-specific extras (booking, e-signatures, AI, in-app calls, your own app) are listed in the pricing section further down.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {whatYouGet.map(({ heading, body }) => (
              <div key={heading} className="rounded-2xl p-6" style={{
                  background: 'var(--glass-bg)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid var(--glass-border)',
                  boxShadow: 'var(--glass-shadow)',
                }}>
                <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${color}18` }}>
                  <CheckCircle2 size={16} style={{ color }} />
                </div>
                <h3 className="mb-1.5 font-bold" style={{ fontFamily: HEAD, color: fg }}>{heading}</h3>
                <p className="text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl px-6">
          <h2 className="mb-10 text-center text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg }}>
            How it works
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {howItWorks.map(({ title, body }, i) => (
              <div key={title} className="flex flex-col rounded-2xl p-6" style={{ background: surface, border: `1px solid ${border}` }}>
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold"
                  style={{ background: `${color}18`, color, fontFamily: HEAD }}>
                  {i + 1}
                </div>
                <h3 className="mb-1.5 font-bold" style={{ fontFamily: HEAD, color: fg }}>{title}</h3>
                <p className="text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="mb-3 text-center text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg }}>
            Simple, transparent pricing
          </h2>
          <p className="mx-auto mb-10 max-w-xl text-center text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
            Pick the plan that fits — a one-off setup to get everything branded and running, then a simple monthly plan to keep it maintained and improving.
          </p>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {tiers.map(({ name, monthly, setup, founding, popular, intro, features }) => (
              <div key={name} className="relative flex flex-col rounded-2xl p-8"
                style={{ background: surface, border: `1px solid ${popular ? GRAD : border}`, boxShadow: popular ? `0 0 0 1px ${GRAD}` : undefined }}>
                {popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold"
                      style={{ background: GRAD, color: '#ffffff', fontFamily: FONT }}>
                      <Sparkles size={12} /> Most popular
                    </span>
                  </div>
                )}
                <h3 className="text-lg font-bold" style={{ fontFamily: HEAD, color: fg }}>{name}</h3>
                <p className="mb-1 mt-2 text-3xl font-bold" style={{ fontFamily: HEAD, color: fg }}>
                  {monthly} <span className="text-sm font-semibold" style={{ color: fgMuted }}>/month</span>
                </p>
                <p className="text-sm font-semibold" style={{ fontFamily: FONT, color: GRAD }}>
                  + {setup}
                </p>
                {founding && (
                  <p className="mb-4 mt-1 text-xs font-semibold" style={{ fontFamily: FONT, color: fgMuted }}>
                    Founding offer — first 5 customers
                  </p>
                )}
                {!founding && <div className="mb-4" />}

                {intro && (
                  <p className="mb-3 text-sm font-semibold" style={{ fontFamily: FONT, color: fg }}>
                    {intro}
                  </p>
                )}
                <ul className="mb-6 flex flex-col gap-3">
                  {features.map(item => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color }} />
                      {item}
                    </li>
                  ))}
                </ul>

                <Link to="/get-started"
                  className="mt-auto inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all hover:opacity-90"
                  style={popular
                    ? { background: GRAD, color: '#ffffff', fontFamily: FONT }
                    : { background: `${color}12`, border: `1px solid ${color}45`, color: fg, fontFamily: FONT }}>
                  Book a demo <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>

          <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
            All plans include hosting, security updates, and a guided demo. No long contracts — cancel with 30 days&apos; notice.
          </p>

          {/* Add-ons — compact subsection of pricing */}
          <div className="mt-10">
            <h3 className="mb-4 text-center text-sm font-bold uppercase tracking-widest" style={{ fontFamily: FONT, color: fgMuted }}>
              Optional add-ons
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {addOns.map(({ title, price, body }) => (
                <div key={title} className="rounded-2xl p-6" style={{ background: surface, border: `1px solid ${border}` }}>
                  <div className="mb-1 flex items-start justify-between gap-3">
                    <h4 className="font-bold leading-snug" style={{ fontFamily: HEAD, color: fg }}>{title}</h4>
                  </div>
                  <p className="mb-2 text-sm font-bold" style={{ fontFamily: FONT, color: GRAD }}>{price}</p>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-8 pb-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="mb-8 text-center text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg }}>
            Frequently asked questions
          </h2>
          <div className="flex flex-col gap-3">
            {faqs.map(f => <FaqItem key={f.q} {...f} />)}
          </div>
        </div>
      </section>

      {/* ── Final CTA — shared wave-backed banner, matches all service pages ── */}
      <ServiceFinalBanner
        heading="Ready for your own branded portal?"
        sub="Book a free discovery call and we'll show you the portal, talk through your business, and map out exactly how it would work under your brand."
        ctaText="Book a free call"
      />
    </>
  );
}
